import { useState, useEffect } from 'react';
import { useAppKitAccount, useAppKitNetwork } from '@reown/appkit/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { QRCodeCanvas } from 'qrcode.react';
import { Wallet, Copy, ExternalLink, CheckCircle2, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { payments as paymentsApi, receipts as receiptsApi } from '@/lib/api';
import { getNetworkName, isTestnet } from '@/config/reownConfig';

const VERIFICATION_STATUSES = {
  idle: { label: 'Ready to Pay', color: 'bg-slate-100 text-slate-700', icon: Clock },
  connecting: { label: 'Connecting Wallet', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
  ready: { label: 'Ready to Send', color: 'bg-teal-100 text-teal-700', icon: Wallet },
  verifying: { label: 'Verifying Payment', color: 'bg-amber-100 text-amber-700', icon: Clock },
  confirmed: { label: 'Payment Confirmed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  failed: { label: 'Payment Failed', color: 'bg-red-100 text-red-700', icon: AlertCircle }
};

export const CryptoPayment = ({ 
  bookingId, 
  hotelWalletAddress, 
  amountUSD, 
  chain,
  onSuccess,
  onCancel,
  testnetMode = false
}) => {
  const { address: userAddress, isConnected } = useAppKitAccount();
  const { chainId } = useAppKitNetwork();
  
  const [status, setStatus] = useState('idle');
  const [txHash, setTxHash] = useState('');
  const [cryptoAmount, setCryptoAmount] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [explorerUrl, setExplorerUrl] = useState('');
  const [progress, setProgress] = useState(0);
  const [verificationAttempts, setVerificationAttempts] = useState(0);

  // Load exchange rate on mount
  useEffect(() => {
    loadExchangeRate();
  }, [chain, amountUSD]);

  // Update status when wallet connects
  useEffect(() => {
    if (isConnected && status === 'idle') {
      setStatus('ready');
    }
  }, [isConnected]);

  const loadExchangeRate = async () => {
    try {
      const conversion = await paymentsApi.convertAmount(amountUSD, chain);
      setCryptoAmount(conversion.crypto_amount);
      setExchangeRate(conversion.rate);
    } catch (error) {
      console.error('Failed to load exchange rate:', error);
      toast.error('Failed to load crypto conversion');
    }
  };

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(hotelWalletAddress);
    toast.success('Wallet address copied!');
  };

  const handleManualVerify = async () => {
    if (!txHash || txHash.trim().length < 10) {
      toast.error('Please enter a valid transaction hash');
      return;
    }

    setStatus('verifying');
    setProgress(20);

    try {
      // For testnet, use mock verification
      if (testnetMode) {
        const result = await paymentsApi.mockVerify(bookingId, chain, amountUSD);
        
        setProgress(80);
        setStatus('confirmed');
        setExplorerUrl(result.explorer_url);
        
        // Generate receipt automatically
        try {
          await paymentsApi.generate(bookingId);
          setProgress(100);
          toast.success('Payment confirmed & receipt generated!');
        } catch (err) {
          setProgress(100);
          toast.success('Payment confirmed!');
          console.log('Receipt generation will happen in background');
        }
        
        setTimeout(() => {
          onSuccess(result.tx_data);
        }, 1500);
      } else {
        // Real verification
        const result = await paymentsApi.verifyTransaction({
          tx_hash: txHash,
          booking_id: bookingId,
          chain,
          expected_amount_usd: amountUSD,
          hotel_wallet_address: hotelWalletAddress,
          testnet: false
        });

        if (result.verified) {
          setProgress(80);
          setStatus('confirmed');
          setExplorerUrl(result.explorer_url);
          
          // Generate receipt automatically
          try {
            await receiptsApi.generate(bookingId);
            setProgress(100);
            toast.success('Payment confirmed & receipt generated!');
          } catch (err) {
            setProgress(100);
            toast.success('Payment confirmed!');
            console.log('Receipt generation will happen in background');
          }
          
          setTimeout(() => {
            onSuccess(result.tx_data);
          }, 1500);
        } else {
          setStatus('failed');
          toast.error(result.message || 'Payment verification failed');
        }
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('failed');
      toast.error('Failed to verify payment');
    }
  };

  const handleAutoVerify = async () => {
    // Auto-polling verification (for when we detect tx from events)
    setStatus('verifying');
    setProgress(40);
    setVerificationAttempts(prev => prev + 1);

    // Simulate verification polling
    const pollInterval = setInterval(async () => {
      setProgress(prev => Math.min(prev + 10, 90));
      
      // In real implementation, this would poll the blockchain
      // For now, we require manual tx hash input
      
    }, 2000);

    setTimeout(() => {
      clearInterval(pollInterval);
      setStatus('ready');
      setProgress(0);
    }, 10000);
  };

  const statusInfo = VERIFICATION_STATUSES[status];
  const StatusIcon = statusInfo.icon;
  const networkName = getNetworkName(chainId);
  const isTestnetNetwork = isTestnet(chainId);

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="crypto-payment-screen">
      <div className="max-w-5xl mx-auto">
        {/* Testnet indicator */}
        {(testnetMode || isTestnetNetwork) && (
          <Alert className="mb-6 bg-amber-50 border-amber-200">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-sm text-amber-900">
              <strong>Testnet Mode:</strong> This is a simulation. No real cryptocurrency will be transferred.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left: Payment Instructions */}
          <div className="lg:col-span-7 space-y-6">
            {/* Status Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="heading-font text-xl">Payment Status</CardTitle>
                  <Badge className={statusInfo.color}>
                    <StatusIcon className={`w-3 h-3 mr-1 ${status === 'connecting' || status === 'verifying' ? 'animate-spin' : ''}`} />
                    {statusInfo.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {status === 'verifying' && (
                  <div className="space-y-2">
                    <Progress value={progress} className="h-2" data-testid="crypto-payment-confirmation-progress" />
                    <p className="text-xs text-muted-foreground text-center">Checking blockchain for your payment...</p>
                  </div>
                )}
                {status === 'confirmed' && (
                  <Alert className="bg-emerald-50 border-emerald-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <AlertDescription className="text-sm text-emerald-900">
                      Payment confirmed! Your booking is now secured.
                    </AlertDescription>
                  </Alert>
                )}
                {status === 'failed' && (
                  <Alert className="bg-red-50 border-red-200">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <AlertDescription className="text-sm text-red-900">
                      Payment verification failed. Please check your transaction and try again.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Wallet Connection */}
            {!isConnected && status === 'idle' && (
              <Card>
                <CardHeader>
                  <CardTitle className="heading-font">Step 1: Connect Your Wallet</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Connect your Web3 wallet to proceed with crypto payment.
                  </p>
                  <appkit-button data-testid="wallet-connect-open-button" />
                </CardContent>
              </Card>
            )}

            {/* Payment Instructions */}
            {isConnected && status !== 'confirmed' && (
              <Card>
                <CardHeader>
                  <CardTitle className="heading-font">Step 2: Send Payment</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-muted/50 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Send to:</span>
                      <Badge variant="outline" className="text-xs">{chain.toUpperCase()}</Badge>
                    </div>
                    
                    <div className="bg-background rounded-lg p-3 border">
                      <p className="text-xs text-muted-foreground mb-1">Hotel Wallet Address</p>
                      <p className="mono text-sm break-all" data-testid="hotel-wallet-address-text">
                        {hotelWalletAddress}
                      </p>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyAddress}
                      className="w-full"
                      data-testid="hotel-wallet-address-copy-button"
                    >
                      <Copy className="w-3 h-3 mr-2" />
                      Copy Address
                    </Button>

                    {/* QR Code */}
                    <div className="flex justify-center py-4">
                      <div className="bg-white p-3 rounded-xl border-2 border-border">
                        <QRCodeCanvas 
                          value={hotelWalletAddress} 
                          size={180}
                          data-testid="hotel-wallet-address-qr"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Manual TX Hash Input */}
                  <div className="space-y-3">
                    <Label htmlFor="tx-hash" className="text-sm font-medium">
                      After sending, paste your transaction hash:
                    </Label>
                    <Input
                      id="tx-hash"
                      data-testid="crypto-payment-tx-hash-input"
                      value={txHash}
                      onChange={(e) => setTxHash(e.target.value)}
                      placeholder={chain === 'solana' ? 'Solana signature...' : '0x...'}
                      className="font-mono text-sm"
                      disabled={status === 'verifying' || status === 'confirmed'}
                    />
                    
                    <Button
                      onClick={handleManualVerify}
                      disabled={!txHash || status === 'verifying' || status === 'confirmed'}
                      className="w-full"
                      data-testid="crypto-payment-verify-button"
                    >
                      {status === 'verifying' ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Verify Payment
                        </>
                      )}
                    </Button>
                  </div>

                  {explorerUrl && (
                    <a
                      href={explorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline flex items-center gap-1 justify-center"
                      data-testid="crypto-payment-explorer-link"
                    >
                      View on block explorer
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Cancel Button */}
            {status !== 'confirmed' && (
              <Button
                variant="outline"
                onClick={onCancel}
                className="w-full"
                disabled={status === 'verifying'}
              >
                Cancel Payment
              </Button>
            )}
          </div>

          {/* Right: Order Summary (Sticky on desktop) */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-6">
              <Card className="border-2 border-primary/20" data-testid="crypto-payment-summary">
                <CardHeader>
                  <CardTitle className="heading-font text-lg">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Amount</span>
                      <span className="font-bold mono text-lg">${amountUSD}</span>
                    </div>
                    
                    {cryptoAmount && exchangeRate && (
                      <>
                        <Separator />
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Crypto Equivalent</span>
                          <span className="font-semibold mono text-primary">
                            {cryptoAmount.toFixed(6)} {chain === 'ethereum' || chain === 'base' ? 'ETH' : chain === 'polygon' ? 'MATIC' : chain === 'bitcoin' ? 'BTC' : 'SOL'}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Exchange Rate</span>
                          <span className="mono">1 {chain === 'ethereum' || chain === 'base' ? 'ETH' : chain === 'polygon' ? 'MATIC' : chain === 'bitcoin' ? 'BTC' : 'SOL'} ≈ ${exchangeRate.toFixed(2)}</span>
                        </div>
                      </>
                    )}
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Network</span>
                      <Badge variant="outline" className="text-xs">{chain.toUpperCase()}</Badge>
                    </div>
                    
                    {isConnected && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Your Wallet</span>
                        <span className="mono text-xs">{userAddress?.slice(0, 6)}...{userAddress?.slice(-4)}</span>
                      </div>
                    )}
                    
                    {networkName && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Connected Network</span>
                        <span className="text-xs">{networkName}</span>
                      </div>
                    )}
                  </div>

                  <Alert className="bg-primary/5 border-primary/20">
                    <AlertDescription className="text-xs">
                      Send <strong className="mono">{cryptoAmount?.toFixed(6)}</strong> to the address above from your wallet. Confirmation usually takes 10-90 seconds.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
